import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

import { useSnackbar } from "notistack";

import { useGlobalModals } from "../../contexts/GlobalModalsContext";
import PageHeader from "../../components/common/PageHeader";
import FilterBar from "../../components/common/FilterBar";
import DataTableWrapper from "../../components/common/DataTableWrapper";
import EmptyState from "../../components/common/EmptyState";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import FormModal from "../../components/common/FormModal";
import ViewModal from "../../components/common/ViewModal";

import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  useCreateClientMutation,
  useGetClientQuery,
  useListClientsQuery,
  useUpdateClientMutation,
  useArchiveClientMutation,
} from "../../features/masterdata/masterdataApi";
import type { Client, ClientCreate, ClientType } from "../../features/masterdata/types";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { usePersistedListFilters } from "../../hooks/usePersistedListFilters";

const CLIENT_TYPES: { value: ClientType; label: string }[] = [
  { value: "individual", label: "Individual" },
  { value: "hotel", label: "Hotel" },
  { value: "hotel_group", label: "Hotel group" },
  { value: "company", label: "Company" },
];

function typeLabel(t: string) {
  return CLIENT_TYPES.find((x) => x.value === t)?.label ?? t;
}

type ClientOrdering = "name" | "-name" | "created_at" | "-created_at";

export default function ClientsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { showError, showConfirm } = useGlobalModals();

  const [filters, setFilters] = usePersistedListFilters("clients", {
    type: "" as ClientType | "",
    search: "",
    ordering: "name" as ClientOrdering,
  });
  const { type, search, ordering } = filters;
  const debouncedSearch = useDebouncedValue(search);

  const [page, setPage] = useState(1);

  const toggleSort = (field: "name" | "created_at") => {
    setFilters((prev) => ({
      ...prev,
      ordering: (prev.ordering === field ? `-${field}` : prev.ordering === `-${field}` ? field : field) as ClientOrdering,
    }));
  };
  const sortIcon = (field: "name" | "created_at") => {
    if (ordering === field) return " ↑";
    if (ordering === `-${field}`) return " ↓";
    return " ⇅";
  };
  const sortAria = (field: "name" | "created_at"): "ascending" | "descending" | "none" =>
    ordering === field ? "ascending" : ordering === `-${field}` ? "descending" : "none";

  // dialogs state
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);

  const [openView, setOpenView] = useState(false);
  const [viewing, setViewing] = useState<Client | null>(null);

  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Client | null>(null);

  const [archiveClient, { isLoading: isArchiving }] = useArchiveClientMutation();

  const [form, setForm] = useState<ClientCreate>({
    type: "hotel",
    name: "",
    contact_name: null,
    email: null,
    phone: null,
    notes: null,
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [type, debouncedSearch, ordering]);

  const { data: editData, isLoading: isLoadingEdit } = useGetClientQuery(
    editing ? editing.id : skipToken
  );

  const { data, isLoading, isFetching, error, refetch } = useListClientsQuery({
    type: type || undefined,
    search: debouncedSearch,
    ordering,
    page,
  });

  const rows = data?.results ?? [];
  const count = data?.count ?? 0;

  const canPrev = !!data?.previous && page > 1;
  const canNext = !!data?.next;

  const [createClient, { isLoading: isCreating }] = useCreateClientMutation();
  const [updateClient, { isLoading: isUpdating }] = useUpdateClientMutation();
  // Delete not supported by backend; confirm dialog shows info message

  const saving = isCreating || isUpdating;

  const showEmpty = !isLoading && !error && rows.length === 0;

  // When editing, sync form from GET-by-id so we have latest data
  useEffect(() => {
    if (editing?.id && editData?.id === editing.id) {
      setForm({
        type: editData.type,
        name: editData.name ?? "",
        contact_name: editData.contact_name ?? null,
        email: editData.email ?? null,
        phone: editData.phone ?? null,
        notes: editData.notes ?? null,
      });
    }
  }, [editing?.id, editData]);

  const onOpenCreate = () => {
    setEditing(null);
    setSubmitAttempted(false);
    setForm({
      type: "hotel",
      name: "",
      contact_name: null,
      email: null,
      phone: null,
      notes: null,
    });
    setOpenForm(true);
  };

  const onOpenEdit = (c: Client) => {
    setSubmitAttempted(false);
    setEditing(c);
    setForm({
      type: c.type,
      name: c.name ?? "",
      contact_name: c.contact_name ?? null,
      email: c.email ?? null,
      phone: c.phone ?? null,
      notes: c.notes ?? null,
    });
    setOpenForm(true);
  };

  const onOpenView = (c: Client) => {
    setViewing(c);
    setOpenView(true);
  };

  const onArchive = async (c: Client) => {
    const confirmed = await showConfirm({
      title: "Archive client?",
      message: `"${c.name}" will be archived and hidden from the default list. You can still include archived items when needed.`,
      confirmText: "Archive",
      cancelText: "Cancel",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await archiveClient({ id: c.id }).unwrap();
      enqueueSnackbar("Client archived", { variant: "success" });
    } catch (e: any) {
      const msg = e?.data?.detail || "Failed to archive client";
      enqueueSnackbar(msg, { variant: "error" });
      showError({ title: "Archive failed", message: typeof msg === "string" ? msg : "Could not archive client.", details: e?.data ? JSON.stringify(e.data, null, 2) : undefined });
    }
  };

  const onSubmitForm = async () => {
    setSubmitAttempted(true);
    if (!form.name.trim()) return;
    try {

      if (!editing) {
        await createClient({
          ...form,
          name: form.name.trim(),
          contact_name: form.contact_name || null,
          email: form.email || null,
          phone: form.phone || null,
          notes: form.notes || null,
        }).unwrap();
        enqueueSnackbar("Client created", { variant: "success" });
      } else {
        await updateClient({
          id: editing.id,
          patch: {
            ...form,
            name: form.name.trim(),
            contact_name: form.contact_name || null,
            email: form.email || null,
            phone: form.phone || null,
            notes: form.notes || null,
          },
        }).unwrap();
        enqueueSnackbar("Client updated", { variant: "success" });
      }

      setOpenForm(false);
      setEditing(null);
      setSubmitAttempted(false);
      setPage(1);
    } catch (e: any) {
      const msg = e?.data?.detail || "Failed to save client";
      enqueueSnackbar(msg, { variant: "error" });
      showError({ title: "Save failed", message: typeof msg === "string" ? msg : "Could not save client.", details: e?.data ? JSON.stringify(e.data, null, 2) : undefined });
    }
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    setOpenDelete(false);
    setDeleteTarget(null);
    setPage(1);
    enqueueSnackbar("Deleting clients is not supported. Deactivate or archive instead.", { variant: "info" });
  };

  const headerRight = useMemo(() => {
    return (
      <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={onOpenCreate}>
        New Client
      </Button>
    );
  }, []);

  return (
    <Box>
      <PageHeader
        title="Clients"
        subtitle="Hotels, hotel groups, companies, individuals"
        actions={headerRight}
      />

      <FilterBar loading={isLoading || isFetching}>
        <TextField
          label="Type"
          select
          value={type}
          onChange={(e) => setFilters({ type: e.target.value as ClientType | "" })}
          size="small"
          sx={{ minWidth: { xs: 0, sm: 180 }, width: { xs: "100%", sm: "auto" } }}
        >
          <MenuItem value="">All</MenuItem>
          {CLIENT_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Search (name, email, contact)"
          value={search}
          onChange={(e) => setFilters({ search: e.target.value })}
          size="small"
          placeholder="Search…"
          sx={{ minWidth: 0, flex: 1, width: { xs: "100%", sm: "auto" } }}
        />
      </FilterBar>

      {showEmpty ? (
        <EmptyState
          title="No clients yet"
          description="Create clients to link transfers and operations."
          actionLabel="Create client"
          onAction={onOpenCreate}
        />
      ) : (
        <>
          <DataTableWrapper
            loading={isLoading}
            error={!!error}
            errorMessage="Failed to load clients."
            onRetry={() => refetch()}
            minWidth={560}
          >
            <Table sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell aria-sort={sortAria("name")}>
                    <Box
                      component="span"
                      onClick={() => toggleSort("name")}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "pointer",
                        fontWeight: 600,
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      Name{sortIcon("name")}
                    </Box>
                  </TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{r.name}</TableCell>

                    <TableCell>
                      <Chip size="small" label={typeLabel(r.type)} />
                    </TableCell>

                    <TableCell>{r.contact_name || "-"}</TableCell>
                    <TableCell>{r.email || "-"}</TableCell>
                    <TableCell>{r.phone || "-"}</TableCell>

                    <TableCell align="right">
                      <IconButton size="small" onClick={() => onOpenView(r)} aria-label="view">
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => onOpenEdit(r)} aria-label="edit">
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      {!r.is_archived && (
                        <Tooltip title="Archive">
                          <IconButton
                            size="small"
                            onClick={() => onArchive(r)}
                            disabled={isArchiving}
                            aria-label="Archive client"
                          >
                            <ArchiveOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DataTableWrapper>

          {/* paging */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              Total: {count} • Page: {page}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={isFetching || !canPrev}
              >
                Prev
              </Button>
              <Button onClick={() => setPage((p) => p + 1)} disabled={isFetching || !canNext}>
                Next
              </Button>
            </Stack>
          </Stack>
        </>
      )}

      <ViewModal
        open={openView}
        onClose={() => setOpenView(false)}
        title="Client details"
        subtitle={viewing ? `${viewing.name} · ${typeLabel(viewing.type)}` : undefined}
        rows={
          viewing
            ? [
                { label: "Contact", value: viewing.contact_name },
                { label: "Email", value: viewing.email },
                { label: "Phone", value: viewing.phone },
                { label: "Notes", value: viewing.notes },
              ]
            : undefined
        }
        onEdit={
          viewing
            ? () => {
                onOpenEdit(viewing);
                setOpenView(false);
              }
            : undefined
        }
      />

      <FormModal
        open={openForm}
        onClose={() => { setSubmitAttempted(false); setOpenForm(false); }}
        title={editing ? "Edit client" : "New client"}
        onSubmit={onSubmitForm}
        submitDisabled={!form.name.trim()}
        loading={saving || (!!editing && isLoadingEdit)}
        maxWidth="sm"
      >
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Type"
              select
              value={form.type}
              onChange={(e) => setForm((p) => ({ ...p, type: e.target.value as ClientType }))}
              fullWidth
            >
              {CLIENT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Company or person name"
              required
              autoFocus
              fullWidth
              error={submitAttempted && !form.name.trim()}
              helperText={submitAttempted && !form.name.trim() ? "Name is required." : undefined}
            />
          </Stack>
          <TextField
            label="Contact name (optional)"
            value={form.contact_name ?? ""}
            onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value || null }))}
            placeholder="Primary contact"
            fullWidth
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Email (optional)"
              type="email"
              value={form.email ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value || null }))}
              placeholder="email@example.com"
              fullWidth
            />
            <TextField
              label="Phone (optional)"
              value={form.phone ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value || null }))}
              placeholder="+30 …"
              fullWidth
            />
          </Stack>
          <TextField
            label="Notes (optional)"
            value={form.notes ?? ""}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value || null }))}
            placeholder="Internal notes"
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </FormModal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={openDelete}
        title="Delete client?"
        description={deleteTarget ? `This will delete "${deleteTarget.name}".` : undefined}
        confirmText="Delete"
        severity="destructive"
        loading={false}
        onClose={() => {
          setOpenDelete(false);
          setDeleteTarget(null);
        }}
        onConfirm={onConfirmDelete}
      />
    </Box>
  );
}
